/**
 * Gestão de usuários Editor (admin). Contratos: docs/contracts/api/admin/platform-users/*.md
 */
import type { AdminMockRequestHandler } from './mock-api-admin.handlers';

import { Observable, of } from 'rxjs';

import { HttpRequest, HttpResponse } from '@angular/common/http';

type MockPlatformEditorRow = {
  creciInscription: string | null;
  creciState: string | null;
  displayName: string;
  email: string;
  id: string;
  isActive: boolean;
  mustChangePassword: boolean;
  phoneDigits: string;
};

function bearerAccess(req: HttpRequest<unknown>): string | null {
  const headerValue = req.headers.get('Authorization');

  if (headerValue == null || !headerValue.startsWith('Bearer '))
    return null;

  return headerValue.slice('Bearer '.length).trim();
}

function jsonFail(status: number, code: string, messageKey: string) {
  return new HttpResponse({
    body: { data: null, errors: [{ code, messageKey }], success: false },
    status,
  });
}

function ok<T>(data: T) {
  return { errors: [] as { code: string; messageKey: string }[], data, success: true };
}

function parseJsonBody(req: HttpRequest<unknown>): Record<string, unknown> | null {
  const rawBody = req.body;

  if (rawBody == null || typeof rawBody !== 'object')
    return null;

  return rawBody as Record<string, unknown>;
}

function provisionalPassword(): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const randomBytes = new Uint8Array(6);

  globalThis.crypto.getRandomValues(randomBytes);

  let token = '';

  for (let index = 0; index < 6; index++)
    token += alphabet[randomBytes[index]! % alphabet.length]!;

  return token;
}

function validatePhoneDigits(digits: string): boolean {
  if (!/^\d{10}$|^\d{11}$/.test(digits))
    return false;

  if (digits.length === 11 && digits[2] !== '9')
    return false;

  return true;
}

function validateCreciPair(state: string | null | undefined, inscription: string | null | undefined): string | null {
  const stateTrimmed = typeof state === 'string' ? state.trim() : '';
  const inscriptionTrimmed = typeof inscription === 'string' ? inscription.trim() : '';
  const hasState = stateTrimmed.length > 0;
  const hasInscription = inscriptionTrimmed.length > 0;

  if (hasState && !hasInscription)
    return 'app.admin.platformUsers.validation.creciInscriptionRequired';

  if (hasInscription && !hasState)
    return 'app.admin.platformUsers.validation.creciStateRequired';

  if (!hasState && !hasInscription)
    return null;

  if (!/^[A-Z]{2}$/.test(stateTrimmed))
    return 'app.admin.platformUsers.validation.creciStateInvalid';

  if (!/^\d+-[FJ]$/.test(inscriptionTrimmed))
    return 'app.admin.platformUsers.validation.creciInscriptionFormat';

  return null;
}

function rowToListElement(row: MockPlatformEditorRow) {
  return {
    creciInscription: row.creciInscription,
    creciState: row.creciState,
    displayName: row.displayName,
    email: row.email,
    id: row.id,
    isActive: row.isActive,
    mustChangePassword: row.mustChangePassword,
    phoneDigits: row.phoneDigits,
    platformRole: 'Editor',
  };
}

function sortRows(rows: MockPlatformEditorRow[]): MockPlatformEditorRow[] {
  return [...rows].sort((rowA, rowB) =>
    rowA.displayName.localeCompare(rowB.displayName, 'pt-BR', { sensitivity: 'base' }),
  );
}

/** Estado mutável por sessão de mock (memória). */
const mockPlatformEditors: MockPlatformEditorRow[] = sortRows([
  {
    creciInscription: null,
    creciState: null,
    displayName: 'Ana Editora',
    email: 'ana.editor@imoveis.local',
    id: '11111111-1111-7111-8111-111111111111',
    isActive: true,
    mustChangePassword: false,
    phoneDigits: '11987654321',
  },
  {
    creciInscription: '12345-F',
    creciState: 'SP',
    displayName: 'Bruno CRECI',
    email: 'bruno.creci@imoveis.local',
    id: '22222222-2222-7222-8222-222222222222',
    isActive: false,
    mustChangePassword: true,
    phoneDigits: '1133334444',
  },
]);

function unauthorized(): Observable<HttpResponse<unknown>> {
  return of(
    jsonFail(401, 'Auth.Unauthorized', 'app.auth.errors.unauthorized'),
  );
}

export const mockApiPlatformUsersHandlers: AdminMockRequestHandler[] = [
  {
    handle: (req) => {
      const token = bearerAccess(req);

      if (token == null || !token.startsWith('mock-access-'))
        return unauthorized();

      return of(
        new HttpResponse({
          body: ok(sortRows(mockPlatformEditors).map(rowToListElement)),
          status: 200,
        }),
      );
    },
    method: 'GET',
    path: /^\/api\/admin\/platform-users$/,
  },
  {
    handle: (req) => {
      const token = bearerAccess(req);

      if (token == null || !token.startsWith('mock-access-'))
        return unauthorized();

      const body = parseJsonBody(req);
      const emailRaw = typeof body?.['email'] === 'string' ? body['email'].trim() : '';
      const displayNameRaw = typeof body?.['displayName'] === 'string' ? body['displayName'].trim() : '';
      const phoneDigitsRaw = typeof body?.['phoneDigits'] === 'string' ? body['phoneDigits'].trim() : '';
      const creciStateRaw = body?.['creciState'];
      const creciInscriptionRaw = body?.['creciInscription'];
      const creciState = typeof creciStateRaw === 'string' ? creciStateRaw.trim() : null;
      const creciInscription =
        typeof creciInscriptionRaw === 'string' ? creciInscriptionRaw.trim() : null;

      if (!emailRaw || !displayNameRaw || !phoneDigitsRaw)
        return of(jsonFail(400, 'Validation.Failed', 'app.admin.platformUsers.validation.required'));

      if (!validatePhoneDigits(phoneDigitsRaw))
        return of(jsonFail(400, 'Validation.Failed', 'app.admin.platformUsers.validation.phoneDigitsInvalid'));

      const creciErrorKey = validateCreciPair(creciState, creciInscription);

      if (creciErrorKey != null)
        return of(jsonFail(400, 'Validation.Failed', creciErrorKey));

      const duplicate = mockPlatformEditors.some((row) => row.email.toLowerCase() === emailRaw.toLowerCase());

      if (duplicate)
        return of(jsonFail(409, 'Conflict.DuplicateEmail', 'app.admin.platformUsers.errors.duplicateEmail'));

      const initialPassword = provisionalPassword();
      const nextRow: MockPlatformEditorRow = {
        creciInscription: creciInscription ?? null,
        creciState: creciState ?? null,
        displayName: displayNameRaw,
        email: emailRaw,
        id: globalThis.crypto.randomUUID(),
        isActive: true,
        mustChangePassword: true,
        phoneDigits: phoneDigitsRaw,
      };

      mockPlatformEditors.push(nextRow);

      const payload = {
        ...rowToListElement(nextRow),
        provisionalPassword: initialPassword,
      };

      return of(
        new HttpResponse({
          body: ok(payload),
          status: 201,
        }),
      );
    },
    method: 'POST',
    path: /^\/api\/admin\/platform-users$/,
  },
  {
    handle: (req, match) => {
      const token = bearerAccess(req);

      if (token == null || !token.startsWith('mock-access-'))
        return unauthorized();

      const userId = match?.[1] ?? '';

      if (!userId)
        return of(jsonFail(404, 'Admin.PlatformUser.NotFound', 'app.admin.platformUsers.errors.notFound'));

      const rowIndex = mockPlatformEditors.findIndex((row) => row.id === userId);

      if (rowIndex < 0)
        return of(jsonFail(404, 'Admin.PlatformUser.NotFound', 'app.admin.platformUsers.errors.notFound'));

      const body = parseJsonBody(req);
      const row = mockPlatformEditors[rowIndex];

      if (body == null)
        return of(
          new HttpResponse({
            body: ok(rowToListElement(row)),
            status: 200,
          }),
        );

      if ('displayName' in body) {
        const displayNamePayload = body['displayName'];

        if (displayNamePayload === null || typeof displayNamePayload !== 'string')
          return of(jsonFail(400, 'Validation.Failed', 'app.admin.platformUsers.validation.required'));

        const displayNameTrimmed = displayNamePayload.trim();

        if (displayNameTrimmed === '')
          return of(jsonFail(400, 'Validation.Failed', 'app.admin.platformUsers.validation.required'));

        row.displayName = displayNameTrimmed;
      }

      if ('isActive' in body) {
        const isActivePayload = body['isActive'];

        if (typeof isActivePayload !== 'boolean')
          return of(jsonFail(400, 'Validation.Failed', 'app.admin.platformUsers.validation.required'));

        row.isActive = isActivePayload;
      }

      if ('phoneDigits' in body) {
        const phoneDigitsPayload = body['phoneDigits'];

        if (phoneDigitsPayload === null || typeof phoneDigitsPayload !== 'string')
          return of(jsonFail(400, 'Validation.Failed', 'app.admin.platformUsers.validation.required'));

        const digits = phoneDigitsPayload.trim();

        if (!validatePhoneDigits(digits))
          return of(jsonFail(400, 'Validation.Failed', 'app.admin.platformUsers.validation.phoneDigitsInvalid'));

        row.phoneDigits = digits;
      }

      let nextState = row.creciState;
      let nextInscription = row.creciInscription;

      if ('creciState' in body) {
        const rawState = body['creciState'];

        nextState = rawState === null ? null : typeof rawState === 'string' ? rawState.trim() : nextState;
      }

      if ('creciInscription' in body) {
        const rawInscription = body['creciInscription'];

        nextInscription =
          rawInscription === null ? null : typeof rawInscription === 'string' ? rawInscription.trim() : nextInscription;
      }

      if ('creciState' in body || 'creciInscription' in body) {
        const pairError = validateCreciPair(nextState ?? undefined, nextInscription ?? undefined);

        if (pairError != null)
          return of(jsonFail(400, 'Validation.Failed', pairError));

        row.creciState = nextState;
        row.creciInscription = nextInscription;
      }

      return of(
        new HttpResponse({
          body: ok(rowToListElement(row)),
          status: 200,
        }),
      );
    },
    method: 'PATCH',
    path: /^\/api\/admin\/platform-users\/([^/]+)$/,
  },
  {
    handle: (req, match) => {
      const token = bearerAccess(req);

      if (token == null || !token.startsWith('mock-access-'))
        return unauthorized();

      const userId = match?.[1] ?? '';

      if (!userId)
        return of(jsonFail(404, 'Admin.PlatformUser.NotFound', 'app.admin.platformUsers.errors.notFound'));

      const row = mockPlatformEditors.find((candidateRow) => candidateRow.id === userId);

      if (row == null)
        return of(jsonFail(404, 'Admin.PlatformUser.NotFound', 'app.admin.platformUsers.errors.notFound'));

      row.mustChangePassword = true;

      return of(
        new HttpResponse({
          body: ok({
            mustChangePassword: true,
            provisionalPassword: provisionalPassword(),
          }),
          status: 200,
        }),
      );
    },
    method: 'POST',
    path: /^\/api\/admin\/platform-users\/([^/]+)\/regenerate-provisional-password$/,
  },
];
